import { useEffect, useState } from 'react'
import { ShieldCheck, AlertTriangle, Clock, Users, Building2, FolderKanban, Activity, Database, CheckCircle2, ExternalLink, Loader2 } from 'lucide-react'
import { useOrg } from '@/lib/orgs'
import { ViewContext } from '@/lib/orgs/types'
import { useUserStats } from '@/lib/data'
import { getPlaneClient } from '@/lib/plane/client'
import { PlaneIssue } from '@/lib/plane/types'

interface DashboardGlobalProps {
  onSelectOrg: (id: ViewContext) => void
}

export function DashboardGlobal({ onSelectOrg }: DashboardGlobalProps) {
  const { organizations, isLoading, buyrClient } = useOrg()
  const buyrStats = useUserStats(buyrClient)

  // Plane.so data
  const [planeStats, setPlaneStats] = useState({
    activeTasks: 0,
    completedThisWeek: 0,
    inReview: 0,
    blocked: 0,
    isLoading: true,
  })

  // Fetch Plane.so stats
  useEffect(() => {
    const fetchPlaneStats = async () => {
      const client = getPlaneClient()
      if (!client.isConfigured()) {
        setPlaneStats(prev => ({ ...prev, isLoading: false }))
        return
      }

      try {
        const { data: projects } = await client.getProjects()
        if (!projects) {
          setPlaneStats(prev => ({ ...prev, isLoading: false }))
          return
        }

        const projectList = Array.isArray(projects) ? projects : (projects as any).results || []

        let activeTasks = 0
        let completedThisWeek = 0
        let inReview = 0
        let blocked = 0

        const oneWeekAgo = new Date()
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7)

        for (const project of projectList) {
          const { data: issues } = await client.getIssues(project.id)
          if (!issues) continue

          const issueList: PlaneIssue[] = Array.isArray(issues) ? issues : (issues as any).results || []

          for (const issue of issueList) {
            const stateName = issue.state_detail?.name?.toLowerCase() || ''

            if (stateName.includes('done') || stateName.includes('complete') || stateName.includes('cancelled')) {
              if (issue.completed_at && new Date(issue.completed_at) >= oneWeekAgo) {
                completedThisWeek++
              }
            } else {
              activeTasks++
              if (stateName.includes('review')) {
                inReview++
              }
              if (stateName.includes('block')) {
                blocked++
              }
            }
          }
        }

        setPlaneStats({
          activeTasks,
          completedThisWeek,
          inReview,
          blocked,
          isLoading: false,
        })
      } catch (error) {
        console.error('Error fetching Plane stats:', error)
        setPlaneStats(prev => ({ ...prev, isLoading: false }))
      }
    }

    fetchPlaneStats()
  }, [])

  const activeOrgs = organizations.filter(org => org.isActive && org.isConfigured)
  const pendingOrgs = organizations.filter(org => !org.isActive || !org.isConfigured)

  // Top-level cross-org metrics
  const globalMetrics = [
    {
      label: 'Active Organizations',
      value: activeOrgs.length.toString(),
      subtext: `${pendingOrgs.length} pending setup`,
      icon: Building2,
      color: 'text-blue-400',
    },
    {
      label: 'Active Tasks',
      value: planeStats.isLoading ? '...' : planeStats.activeTasks.toString(),
      subtext: 'Across all projects',
      icon: FolderKanban,
      color: 'text-purple-400',
    },
    {
      label: 'Completed This Week',
      value: planeStats.isLoading ? '...' : planeStats.completedThisWeek.toString(),
      subtext: 'Tasks finished',
      icon: CheckCircle2,
      color: 'text-emerald-400',
    },
    {
      label: 'Live Events',
      value: buyrStats.isConnected ? 'Connected' : 'Offline',
      subtext: buyrStats.isConnected ? `${buyrStats.events.length} recent` : 'Reconnecting...',
      icon: Activity,
      color: buyrStats.isConnected ? 'text-emerald-400' : 'text-yellow-400',
    },
  ]

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[var(--text-main)] mb-2">Global Overview</h1>
        <p className="text-[var(--text-muted)]">
          Cross-organization dashboard. {activeOrgs.length} active org{activeOrgs.length !== 1 ? 's' : ''}, {pendingOrgs.length} pending setup.
        </p>
      </div>

      {/* Top Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {globalMetrics.map((metric, index) => {
          const Icon = metric.icon
          return (
            <div
              key={index}
              className="bg-[var(--bg-card)] border border-[var(--border)] p-6 rounded-xl hover:border-[var(--primary)] transition-colors duration-300"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <Icon size={18} className={metric.color} />
                  <h3 className="text-[var(--text-muted)] text-sm font-medium">{metric.label}</h3>
                </div>
              </div>
              <p className="text-2xl font-bold text-[var(--text-main)]">{metric.value}</p>
              <p className="text-xs text-[var(--text-muted)] mt-1">{metric.subtext}</p>
            </div>
          )
        })}
      </div>

      {/* Organizations Grid + System Health */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Organizations Status */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-[var(--text-main)]">Organizations</h2>
            <span className="text-xs text-[var(--text-muted)]">{organizations.length} total</span>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
            </div>
          ) : (
            <div className="space-y-3">
              {organizations.map((org) => {
                const OrgIcon = org.icon
                const isActive = org.isActive && org.isConfigured

                return (
                  <div
                    key={org.slug}
                    onClick={() => isActive && onSelectOrg(org.slug)}
                    className={`group flex items-center p-4 rounded-lg border transition-all ${
                      isActive
                        ? 'bg-[var(--bg-element)] border-[var(--border)] hover:border-[var(--primary)] cursor-pointer'
                        : 'bg-[var(--bg-element)]/50 border-[var(--border)]/50 opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      isActive ? 'bg-[var(--bg-hover)]' : 'bg-[var(--bg-element)]'
                    }`}>
                      <OrgIcon className={org.color} size={24} />
                    </div>
                    <div className="ml-4 flex-1 min-w-0">
                      <h4 className="text-[var(--text-main)] font-semibold">{org.name}</h4>
                      <p className="text-[var(--text-muted)] text-sm truncate">{org.description}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {isActive ? (
                        <>
                          <span className="flex items-center text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded">
                            <ShieldCheck size={12} className="mr-1" />
                            Active
                          </span>
                          <ExternalLink size={16} className="text-[var(--text-muted)] group-hover:text-[var(--primary)] transition-colors" />
                        </>
                      ) : org.isConfigured ? (
                        <span className="flex items-center text-xs text-yellow-400 bg-yellow-500/10 px-2 py-1 rounded">
                          <AlertTriangle size={12} className="mr-1" />
                          Inactive
                        </span>
                      ) : (
                        <span className="flex items-center text-xs text-[var(--text-muted)] bg-[var(--bg-element)] px-2 py-1 rounded">
                          <Clock size={12} className="mr-1" />
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* System Health / Database Connections */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-[var(--text-main)]">System Health</h2>
            <span className="flex items-center text-xs text-emerald-400">
              <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse" />
              All Systems Operational
            </span>
          </div>
          <div className="space-y-4">
            {/* Database Connections */}
            <div>
              <h3 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">Database Connections</h3>
              <div className="space-y-2">
                {organizations.map((org) => {
                  const isConnected = org.isActive && org.isConfigured
                  return (
                    <div key={org.slug} className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-element)]">
                      <div className="flex items-center gap-2">
                        <Database size={14} className={org.color} />
                        <span className="text-sm text-[var(--text-main)]">{org.name} DB</span>
                      </div>
                      <span className={`flex items-center text-xs ${isConnected ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-2 ${isConnected ? 'bg-emerald-400' : 'bg-gray-500'}`} />
                        {isConnected ? 'Connected' : 'Not configured'}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Services Status */}
            <div>
              <h3 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-3">Services</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-element)]">
                  <span className="text-sm text-[var(--text-main)]">Plane.so</span>
                  <span className="flex items-center text-xs text-emerald-400">
                    <span className="w-1.5 h-1.5 rounded-full mr-2 bg-emerald-400" />
                    OK
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-element)]">
                  <span className="text-sm text-[var(--text-main)]">Live Events</span>
                  <span className={`flex items-center text-xs ${buyrStats.isConnected ? 'text-emerald-400' : 'text-yellow-400'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full mr-2 ${buyrStats.isConnected ? 'bg-emerald-400' : 'bg-yellow-400'}`} />
                    {buyrStats.isConnected ? 'OK' : 'Reconnecting'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-element)]">
                  <span className="text-sm text-[var(--text-main)]">Mentatix</span>
                  <span className="flex items-center text-xs text-emerald-400">
                    <span className="w-1.5 h-1.5 rounded-full mr-2 bg-emerald-400" />
                    OK
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-element)]">
                  <span className="text-sm text-[var(--text-main)]">Auth</span>
                  <span className="flex items-center text-xs text-emerald-400">
                    <span className="w-1.5 h-1.5 rounded-full mr-2 bg-emerald-400" />
                    OK
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Management + Live Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plane.so Project Management */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-main)]">Project Management</h2>
              <p className="text-sm text-[var(--text-muted)]">Plane.so tasks across all orgs</p>
            </div>
            {planeStats.isLoading && <Loader2 className="h-5 w-5 animate-spin text-[var(--text-muted)]" />}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-[var(--bg-element)] rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-[var(--text-main)]">
                {planeStats.isLoading ? '...' : planeStats.activeTasks}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Active Tasks</p>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-emerald-400">
                {planeStats.isLoading ? '...' : planeStats.completedThisWeek}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Done This Week</p>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-blue-400">
                {planeStats.isLoading ? '...' : planeStats.inReview}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">In Review</p>
            </div>
            <div className="bg-rose-500/10 border border-rose-500/20 rounded-lg p-4 text-center">
              <p className="text-3xl font-bold text-rose-400">
                {planeStats.isLoading ? '...' : planeStats.blocked}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">Blocked</p>
            </div>
          </div>
        </div>

        {/* Live Activity Feed */}
        <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-main)]">Live Activity</h2>
              <p className="text-sm text-[var(--text-muted)]">Recent events across all orgs</p>
            </div>
            {buyrStats.isConnected && (
              <span className="flex items-center text-xs text-emerald-400">
                <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse" />
                Live
              </span>
            )}
          </div>
          <div className="space-y-3 max-h-[260px] overflow-y-auto custom-scrollbar">
            {buyrStats.events.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)]">
                <Activity size={32} className="mx-auto mb-3 opacity-50" />
                <p className="text-sm">Waiting for events...</p>
                <p className="text-xs mt-1">Activity from all organizations will appear here</p>
              </div>
            ) : (
              buyrStats.events.slice(0, 8).map((event, i) => (
                <div
                  key={`${event.id}-${i}`}
                  className={`flex items-center p-3 rounded-lg border transition-all ${
                    i === 0 ? 'bg-[var(--primary)]/5 border-[var(--primary)]/20' : 'bg-[var(--bg-element)] border-[var(--border)]'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    event.type === 'new_user' ? 'bg-blue-500/20' : 'bg-amber-500/20'
                  }`}>
                    <Users size={14} className={event.type === 'new_user' ? 'text-blue-400' : 'text-amber-400'} />
                  </div>
                  <div className="ml-3 flex-1 min-w-0">
                    <p className="text-sm text-[var(--text-main)] truncate">
                      {event.name || event.email || 'Anonymous'}
                    </p>
                    <p className="text-xs text-[var(--text-muted)]">
                      {event.type === 'new_user' ? 'New signup' : `Premium (${event.billingPeriod})`} • {new Date(event.timestamp).toLocaleTimeString()}
                    </p>
                  </div>
                  <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400 flex-shrink-0">
                    Buy'r
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-6">
        <h2 className="text-lg font-semibold text-[var(--text-main)] mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => onSelectOrg('buyr')}
            className="flex flex-col items-center p-4 rounded-lg bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] border border-[var(--border)] hover:border-[var(--primary)] transition-all"
          >
            <Users size={24} className="text-blue-400 mb-2" />
            <span className="text-sm text-[var(--text-main)]">View Buy'r</span>
            <span className="text-xs text-[var(--text-muted)]">User analytics</span>
          </button>
          <button
            onClick={() => window.open(import.meta.env.VITE_PLANE_URL || 'https://plane.so', '_blank')}
            className="flex flex-col items-center p-4 rounded-lg bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] border border-[var(--border)] hover:border-[var(--primary)] transition-all"
          >
            <FolderKanban size={24} className="text-purple-400 mb-2" />
            <span className="text-sm text-[var(--text-main)]">Open Plane</span>
            <span className="text-xs text-[var(--text-muted)]">Project board</span>
          </button>
          <button
            className="flex flex-col items-center p-4 rounded-lg bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] border border-[var(--border)] hover:border-[var(--primary)] transition-all opacity-50 cursor-not-allowed"
            disabled
          >
            <Building2 size={24} className="text-emerald-400 mb-2" />
            <span className="text-sm text-[var(--text-main)]">Add Org</span>
            <span className="text-xs text-[var(--text-muted)]">Coming soon</span>
          </button>
          <button
            className="flex flex-col items-center p-4 rounded-lg bg-[var(--bg-element)] hover:bg-[var(--bg-hover)] border border-[var(--border)] hover:border-[var(--primary)] transition-all opacity-50 cursor-not-allowed"
            disabled
          >
            <Database size={24} className="text-cyan-400 mb-2" />
            <span className="text-sm text-[var(--text-main)]">Settings</span>
            <span className="text-xs text-[var(--text-muted)]">Configure</span>
          </button>
        </div>
      </div>
    </div>
  )
}
